
import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Conversation } from '@/types/chat';
import { formatDistanceToNow } from 'date-fns';

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversationId: number | string | null;
  onSelectConversation: (id: number | string) => void;
  emptyStateMessage?: string;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  emptyStateMessage = "No conversations yet"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredConversations = searchTerm.trim() 
    ? conversations.filter(c => 
        c.participantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : conversations;
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <Card className="h-[calc(100vh-200px)]">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-lg">Conversations</CardTitle>
        <div className="relative mt-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search conversations..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent className="p-3 overflow-auto h-[calc(100%-80px)]">
        {filteredConversations.length > 0 ? (
          <div className="space-y-2">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-secondary/20 transition-colors ${
                  selectedConversationId === conversation.id ? 'bg-secondary/30' : ''
                }`}
                onClick={() => onSelectConversation(conversation.id)}
              >
                <Avatar>
                  {conversation.participantAvatar ? (
                    <AvatarImage src={conversation.participantAvatar} alt={conversation.participantName} />
                  ) : (
                    <AvatarFallback>{getInitials(conversation.participantName)}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-medium truncate">{conversation.participantName}</h3>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(conversation.lastMessageTime), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground truncate max-w-[80%]">
                      {conversation.lastMessage}
                    </p>
                    {conversation.unreadCount > 0 && (
                      <Badge variant="destructive" className="h-5 w-5 rounded-full p-0 flex items-center justify-center">
                        {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                      </Badge>
                    )}
                  </div>
                  {conversation.condition && (
                    <p className="text-xs text-muted-foreground mt-1">{conversation.condition}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-muted-foreground text-center">
              {searchTerm ? "No matching conversations found" : emptyStateMessage}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationList;
